import React, { useEffect, useState } from "react";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, TextField, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import api from "../../services/api";
import CompanyTransferPlanDialog from "./CompanyTransferPlanDialog";
import { makeStyles, alpha } from "@material-ui/core/styles";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";

const useStyles = makeStyles(theme => ({
  "@keyframes transferAttention": {
    "0%, 100%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}` },
    "50%": { boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0.22)}` }
  },
  transferButton: {
    margin: "8px 0 16px",
    maxWidth: "100%",
    borderWidth: 2,
    fontWeight: 700,
    textAlign: "left",
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    // Two gentle pulses, then remain highlighted without distracting from the form.
    animation: "$transferAttention 2.2s ease-in-out 2",
    "&:hover, &:focus-visible, &.Mui-disabled": { animation: "none" },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" }
  }
}));

const encodeFile = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1]);
  reader.onerror = () => reject(new Error("No fue posible leer el archivo."));
  reader.readAsDataURL(file);
});

export default function ExistingApiRegistrationDialog({ customer, onClose }) {
  return process.env.REACT_APP_ENABLE_COMPANY_TRANSFER_PLANNER === 'true'
    ? <CompanyTransferPlanDialog customer={customer} onClose={onClose} />
    : <LegacyExistingApiRegistrationDialog customer={customer} onClose={onClose} />;
}

function LegacyExistingApiRegistrationDialog({ customer, onClose }) {
  const classes = useStyles();
  const [sources, setSources] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [originsLoading, setOriginsLoading] = useState(true);
  const [originWarning, setOriginWarning] = useState('');
  const [renewal, setRenewal] = useState(false);
  const [form, setForm] = useState({ originId: "", destinationId: "", password: "", rut: null, certificate: null });
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [phase, setPhase] = useState("");
  const [showCopyPlan, setShowCopyPlan] = useState(false);
  useEffect(() => {
    let active = true;
    Promise.all([api.get("/clients/sources/alta-rut"), api.get(`/clients/${customer.id}/transfer-origins`)]).then(([sourceResult, originResult]) => {
      if (!active) return;
      setSources(Array.isArray(sourceResult.data) ? sourceResult.data : []);
      const found = originResult.data.origins || [];
      setOrigins(found);
      setForm(previous => ({ ...previous, originId: found.length === 1 ? String(found[0].id) : '' }));
      setOriginWarning(originResult.data.unavailable?.length ? 'Algunas APIs no respondieron. Solo se muestran los origenes confirmados.' : !found.length ? 'No se encontro la empresa en las APIs consultadas.' : '');
    }).catch(() => { if (active) setError("No fue posible consultar las APIs de origen. Cierre y vuelva a abrir para reintentar."); })
      .finally(() => { if (active) setOriginsLoading(false); });
    return () => { active = false; };
  }, [customer.id]);
  const change = (key, value) => {
    setForm(previous => ({ ...previous, [key]: value, ...(key === 'originId' ? { destinationId: '' } : {}) }));
    setPreview(null); setConfirmed(false); setError("");
  };
  const run = async create => {
    setError("");
    if (!form.originId || !form.destinationId || form.originId === form.destinationId || !form.rut || !form.certificate || !form.password) {
      setError("Seleccione APIs distintas, adjunte el RUT y el certificado renovado con su clave."); return;
    }
    if (form.rut.size > 8 * 1024 * 1024 || form.certificate.size > 4 * 1024 * 1024) {
      setError("El RUT admite hasta 8 MB y el certificado hasta 4 MB."); return;
    }
    if (create && (!confirmed || !preview)) return;
    setBusy(true);
    setPhase(create ? "Registrando en destino. No cierre ni reenvie la solicitud..." : "Consultando origen, destino, RUT y certificado. Puede tomar unos momentos...");
    try {
      const [pdf, certificate] = await Promise.all([encodeFile(form.rut), encodeFile(form.certificate)]);
      const { data } = await api.post(`/clients/${customer.id}/existing-api-registration/${create ? "confirm" : "preview"}`, {
        originId: form.originId, destinationId: form.destinationId, pdf_base64: pdf,
        certificate_base64: certificate, certificate_password: form.password,
        ...(create ? { previewId: preview.previewId, confirm: true } : {})
      }, { timeout: 240000 });
      if (create) { setSuccess(data.message); setForm(previous => ({ ...previous, password: "", rut: null, certificate: null })); }
      else { setPreview(data); setConfirmed(false); }
    } catch (failure) {
      setError(failure.response?.data?.message || "No se confirmo la respuesta. Si estaba creando, consulte la empresa en destino antes de intentar nuevamente.");
      setPreview(null); setConfirmed(false);
    } finally { setBusy(false); setPhase(""); }
  };
  if (showCopyPlan) return <CompanyTransferPlanDialog customer={customer} initialOriginId={form.originId} initialDestinationId={form.destinationId} onClose={() => setShowCopyPlan(false)} />;
  return (
    <Dialog open fullWidth maxWidth="md" onClose={busy ? undefined : onClose} disableEscapeKeyDown={busy}>
      <DialogTitle>Registrar en otra API / Retomar servicio</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>{customer.name} · {customer.identificationNumber}</Typography>
        {process.env.REACT_APP_ENABLE_COMPANY_TRANSFER_PLANNER === "true" && <Button color="primary" variant="outlined" className={classes.transferButton} startIcon={<CompareArrowsIcon />} disabled={busy} onClick={() => setShowCopyPlan(true)}>Planificar copia completa / Completar existente</Button>}
        <Alert severity="info">Empresa ya habilitada: se conserva el Software ID y el PIN de origen para factura electronica. No se ejecutan pruebas DIAN ni se modifica la empresa de origen.</Alert>
        <Alert severity="info" style={{ marginTop: 8 }}>Para reutilizar los datos existentes, abra el plan de copia y seleccione Certificado digital. Se utiliza el certificado y su clave del origen, sin volver a adjuntar RUT ni archivos. Los datos diferentes que ya existan en destino se conservan y se reportan para revision.</Alert>
        {originWarning && <Alert severity="warning">{originWarning}</Alert>}
        {error && <Alert severity="error" style={{ marginTop: 12 }}>{error}</Alert>}
        {success ? <Alert severity="success" style={{ marginTop: 12 }}>{success}</Alert> : <>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} sm={6}><TextField id="registration-origin" select fullWidth variant="outlined" label="API de origen" value={form.originId} disabled={busy || originsLoading || origins.length < 2} helperText={originsLoading ? 'Buscando la empresa...' : origins.length === 1 ? 'Unico origen confirmado.' : 'Seleccione una de las APIs donde existe la empresa.'} onChange={e => change("originId", e.target.value)}>
              {origins.map(source => <MenuItem key={source.id} value={String(source.id)}>{source.name}</MenuItem>)}
            </TextField></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-destination" select fullWidth variant="outlined" label="API de destino" value={form.destinationId} disabled={busy} onChange={e => change("destinationId", e.target.value)}>
              {sources.filter(source => source.enabledForRutOnboarding && String(source.id) !== form.originId).map(source => <MenuItem key={source.id} value={String(source.id)}>{source.name}</MenuItem>)}
            </TextField></Grid>
            <Grid item xs={12}><FormControlLabel control={<Checkbox checked={renewal} disabled={busy} onChange={e => { setRenewal(e.target.checked); setPreview(null); setConfirmed(false); }} />} label="Usar el formulario alternativo de alta con RUT y certificado renovado" /></Grid>
            {renewal && <><Grid item xs={12}><Alert severity="warning">Esta ruta alternativa requiere RUT, certificado nuevo y su clave. Para conservar el certificado del origen utilice el plan de copia, sin adjuntar archivos. No reemplaza certificados existentes en destino.</Alert></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-rut" type="file" fullWidth variant="outlined" label="RUT actualizado" InputLabelProps={{ shrink: true }} inputProps={{ accept: ".pdf" }} disabled={busy} onChange={e => change("rut", e.target.files?.[0])} /></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-certificate" type="file" fullWidth variant="outlined" label="Certificado renovado" InputLabelProps={{ shrink: true }} inputProps={{ accept: ".p12,.pfx" }} disabled={busy} onChange={e => change("certificate", e.target.files?.[0])} /></Grid>
            <Grid item xs={12} sm={6}><TextField id="registration-password" type="password" autoComplete="new-password" fullWidth variant="outlined" label="Clave del certificado" value={form.password} disabled={busy} onChange={e => change("password", e.target.value)} helperText="No se guarda en borradores ni auditorias de SOLUWHATS." /></Grid>
            </>}
          </Grid>
          {preview && <div style={{ marginTop: 16 }}>
            <Typography variant="h6">Revise antes de registrar</Typography>
            <Typography>{preview.name} · NIT {preview.nit}</Typography>
            <Typography>{preview.origin} → {preview.destination}</Typography>
            <Typography>Software ID: {preview.softwareId}</Typography>
            <Typography>Ambiente: {preview.environment} · Operacion: {preview.operationCode}</Typography>
            <Typography>Certificado vigente hasta: {preview.certificateExpires}</Typography>
            <Alert severity="warning" style={{ marginTop: 12 }}>{preview.notice} No utilice simultaneamente el mismo prefijo y consecutivo en ambas APIs.</Alert>
            <FormControlLabel control={<Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} disabled={busy} color="primary" />} label="Confirmo el mismo software habilitado y autorizo crear unicamente en la API de destino." />
          </div>}
        </>}
        {busy && <Typography role="status" style={{ marginTop: 12 }}>{phase}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cerrar</Button>
        {!success && !renewal && process.env.REACT_APP_ENABLE_COMPANY_TRANSFER_PLANNER === 'true' && <Button onClick={() => setShowCopyPlan(true)} disabled={busy || !form.originId || !form.destinationId} variant="contained" color="primary">Continuar con datos del origen</Button>}
        {!success && renewal && <Button onClick={() => run(false)} disabled={busy} variant="outlined" color="primary">Validar sin crear</Button>}
        {!success && renewal && <Button onClick={() => run(true)} disabled={busy || !preview || !confirmed} variant="contained" color="primary">Registrar en destino</Button>}
      </DialogActions>
    </Dialog>
  );
}
